import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBagIcon, MapPinIcon, TrashIcon, EyeIcon, MagnifyingGlassIcon, XMarkIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

const Orders = ({ user }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderFormData, setOrderFormData] = useState({
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    pin: '',
    status: 'pending_shipping',
    quantity: '',
    rate: ''
  });
  const [summaryStats, setSummaryStats] = useState({
    today: 0,
    thisWeek: 0,
    thisMonth: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    dateRange: 'all',
    specificDate: '',
    rateMin: '',
    rateMax: '',
    quantityMin: '',
    quantityMax: ''
  });

  const filterOrders = useCallback((ordersData, searchQuery, activeFilters) => {
    let filtered = [...ordersData];
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(order => {
        const leadName = order.leads?.name?.toLowerCase() || '';
        const leadPhone = order.leads?.phone?.toLowerCase() || '';
        const address = `${order.address_line_1 || ''} ${order.address_line_2 || ''} ${order.city || ''} ${order.state || ''} ${order.pin || ''}`.toLowerCase();
        const status = order.status?.toLowerCase() || '';
        const quantity = order.quantity?.toString() || '';
        const rate = order.rate?.toString() || '';
        
        return leadName.includes(query) ||
               leadPhone.includes(query) ||
               address.includes(query) ||
               status.includes(query) ||
               quantity.includes(query) ||
               rate.includes(query);
      });
    }
    
    // Apply status filter
    if (activeFilters.status !== 'all') {
      filtered = filtered.filter(order => order.status === activeFilters.status);
    }
    
    // Apply date range filter
    if (activeFilters.dateRange !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      switch (activeFilters.dateRange) {
        case 'today':
          filtered = filtered.filter(order => {
            const orderDate = new Date(order.conversion_date);
            return orderDate >= today && orderDate < new Date(today.getTime() + 24 * 60 * 60 * 1000);
          });
          break;
        case 'this_week':
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - today.getDay());
          filtered = filtered.filter(order => new Date(order.conversion_date) >= startOfWeek);
          break;
        case 'this_month':
          const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          filtered = filtered.filter(order => new Date(order.conversion_date) >= startOfMonth);
          break;
        case 'last_30_days':
          const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          filtered = filtered.filter(order => new Date(order.conversion_date) >= last30Days);
          break;
        case 'specific_date':
          if (activeFilters.specificDate) {
            const selectedDate = new Date(activeFilters.specificDate);
            selectedDate.setHours(0, 0, 0, 0);
            const selectedDateEnd = new Date(selectedDate);
            selectedDateEnd.setHours(23, 59, 59, 999);
            filtered = filtered.filter(order => {
              const orderDate = new Date(order.conversion_date);
              return orderDate >= selectedDate && orderDate <= selectedDateEnd;
            });
          }
          break;
        default:
          break;
      }
    }
    
    // Apply rate range filter
    if (activeFilters.rateMin) {
      filtered = filtered.filter(order => (order.rate || 0) >= parseFloat(activeFilters.rateMin));
    }
    if (activeFilters.rateMax) {
      filtered = filtered.filter(order => (order.rate || 0) <= parseFloat(activeFilters.rateMax));
    }
    
    // Apply quantity range filter
    if (activeFilters.quantityMin) {
      filtered = filtered.filter(order => (order.quantity || 0) >= parseInt(activeFilters.quantityMin));
    }
    if (activeFilters.quantityMax) {
      filtered = filtered.filter(order => (order.quantity || 0) <= parseInt(activeFilters.quantityMax));
    }
    
    return filtered;
  }, []);

  // Highlight search terms in text
  const highlightSearchTerm = useCallback((text, searchTerm) => {
    if (!searchTerm.trim() || !text) return text;
    
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 px-1 rounded">
          {part}
        </mark>
      ) : part
    );
  }, []);

  // Handle search and filter changes
  useEffect(() => {
    const filtered = filterOrders(orders, searchTerm, filters);
    setFilteredOrders(filtered);
  }, [orders, searchTerm, filters, filterOrders]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilters({
      status: 'all',
      dateRange: 'all',
      specificDate: '',
      rateMin: '',
      rateMax: '',
      quantityMin: '',
      quantityMax: ''
    });
  };

  const hasActiveFilters = searchTerm || filters.status !== 'all' || filters.dateRange !== 'all' || 
    filters.rateMin || filters.rateMax || filters.quantityMin || filters.quantityMax;

  const calculateSummaryStats = useCallback((ordersData) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    const todayOrders = ordersData.filter(order => {
      const orderDate = new Date(order.conversion_date);
      return orderDate >= today;
    });

    const thisWeekOrders = ordersData.filter(order => {
      const orderDate = new Date(order.conversion_date);
      return orderDate >= startOfWeek;
    });

    const thisMonthOrders = ordersData.filter(order => {
      const orderDate = new Date(order.conversion_date);
      return orderDate >= startOfMonth;
    });

    setSummaryStats({
      today: todayOrders.length,
      thisWeek: thisWeekOrders.length,
      thisMonth: thisMonthOrders.length
    });
  }, []);

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Loading orders for user:', user.id);
      
      // Fetch orders first
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select('*')
        .eq('user_id', user.id)
        .order('conversion_date', { ascending: false });

      if (ordersError) throw ordersError;
      
      console.log('Raw orders data:', ordersData);
      
      // If we have orders, fetch the corresponding leads
      if (ordersData && ordersData.length > 0) {
        const leadIds = ordersData.map(order => order.lead_id);
        console.log('Lead IDs to fetch:', leadIds);
        
        const { data: leadsData, error: leadsError } = await supabase
          .from('leads')
          .select('id, name, phone')
          .in('id', leadIds);
          
        if (leadsError) {
          console.error('Error fetching leads:', leadsError);
          // Continue with orders even if leads fetch fails
        }
        
        // Combine orders with lead data
        const ordersWithLeads = ordersData.map(order => {
          const lead = leadsData?.find(l => l.id === order.lead_id);
          return {
            ...order,
            leads: lead || { name: 'Unknown', phone: 'Unknown' }
          };
        });
        
        console.log('Orders with leads:', ordersWithLeads);
        setOrders(ordersWithLeads);
        calculateSummaryStats(ordersData);
      } else {
        setOrders([]);
        calculateSummaryStats([]);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
      toast.error(`Error loading orders: ${error.message}`);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [user.id, calculateSummaryStats]);

  const setupRealtimeSubscription = useCallback(() => {
    const subscription = supabase
      .channel('orders_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          loadOrders();
        }
      )
      .subscribe((status) => {
        console.log('Orders real-time subscription status:', status);
      });

    window.ordersSubscription = subscription;
  }, [user.id, loadOrders]);

  const cleanupRealtimeSubscription = () => {
    if (window.ordersSubscription) {
      console.log('Cleaning up orders real-time subscription');
      supabase.removeChannel(window.ordersSubscription);
      window.ordersSubscription = null;
    }
  };

  useEffect(() => {
    if (user) {
      loadOrders();
      setupRealtimeSubscription();
    }

    return () => {
      if (user) {
        cleanupRealtimeSubscription();
      }
    };
  }, [user, loadOrders, setupRealtimeSubscription]);

  const handleOrderAction = (order) => {
    setSelectedOrder(order);
    // Initialize form data with order data
    setOrderFormData({
      address_line_1: order.address_line_1 || '',
      address_line_2: order.address_line_2 || '',
      city: order.city || '',
      state: order.state || '',
      pin: order.pin || '',
      status: order.status || 'pending_shipping',
      quantity: order.quantity || '',
      rate: order.rate || ''
    });
    setShowOrderModal(true);
  };

  const openOrderDetails = (order) => {
    navigate(`/orders/${order.id}`);
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('orders')
        .delete()
        .eq('id', orderId);
      
      if (error) throw error;
      
      toast.success('Order deleted successfully');
      loadOrders();
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error(`Error deleting order: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderAddress = async (orderId, addressData) => {
    try {
      const { error } = await supabase
        .from('orders')
        .update({
          address_line_1: addressData.address_line_1,
          address_line_2: addressData.address_line_2,
          city: addressData.city,
          state: addressData.state,
          pin: addressData.pin,
          status: addressData.status,
          quantity: addressData.quantity,
          rate: addressData.rate
        })
        .eq('id', orderId);

      if (error) throw error;
      
      toast.success('Order updated successfully');
      setShowOrderModal(false);
      loadOrders();
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error(`Error updating order: ${error.message}`);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending_shipping':
        return 'bg-yellow-100 text-yellow-800';
      case 'shipped':
        return 'bg-blue-100 text-blue-800';
      case 'out_for_delivery':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'not_redeemed':
        return 'bg-orange-100 text-orange-800';
      case 'redeemed':
        return 'bg-emerald-100 text-emerald-800';
      case 'returned':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Orders</h1>
        <p className="text-gray-600">Manage your converted leads and their orders.</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                <ShoppingBagIcon className="h-5 w-5 text-green-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Today's Orders</p>
              <p className="text-2xl font-semibold text-gray-900">{summaryStats.today}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                <ShoppingBagIcon className="h-5 w-5 text-blue-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">This Week</p>
              <p className="text-2xl font-semibold text-gray-900">{summaryStats.thisWeek}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-purple-100 rounded-full flex items-center justify-center">
                <ShoppingBagIcon className="h-5 w-5 text-purple-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">This Month</p>
              <p className="text-2xl font-semibold text-gray-900">{summaryStats.thisMonth}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Order Management</h3>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center px-4 py-2 rounded-md text-sm ${
                  showFilters 
                    ? 'bg-blue-100 text-blue-700' 
                    : hasActiveFilters 
                    ? 'bg-gray-100 text-gray-700' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                Filters
                {hasActiveFilters && !showFilters && (
                  <span className="ml-2 bg-blue-600 text-white text-xs rounded-full px-2 py-0.5">
                    Active
                  </span>
                )}
              </button>
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  Clear All
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="pending_shipping">Pending Shipping</option>
                  <option value="shipped">Shipped</option>
                  <option value="out_for_delivery">Out For Delivery</option>
                  <option value="delivered">Delivered</option>
                  <option value="not_redeemed">Not Redeemed</option>
                  <option value="redeemed">Redeemed</option>
                  <option value="returned">Returned</option>
                </select>
              </div>

              {/* Date Range Filter */}
              <div className={filters.dateRange === 'specific_date' ? 'md:col-span-2' : ''}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date Range
                </label>
                <select
                  value={filters.dateRange}
                  onChange={(e) => {
                    handleFilterChange('dateRange', e.target.value);
                    if (e.target.value !== 'specific_date') {
                      handleFilterChange('specificDate', '');
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="all">All Dates</option>
                  <option value="today">Today</option>
                  <option value="this_week">This Week</option>
                  <option value="this_month">This Month</option>
                  <option value="last_30_days">Last 30 Days</option>
                  <option value="specific_date">Specific Date</option>
                </select>
              </div>

              {/* Specific Date Input */}
              {filters.dateRange === 'specific_date' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Date
                  </label>
                  <input
                    type="date"
                    value={filters.specificDate}
                    onChange={(e) => handleFilterChange('specificDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              )}

              {/* Rate Range - Min */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Rate (₹)
                </label>
                <input
                  type="number"
                  value={filters.rateMin}
                  onChange={(e) => handleFilterChange('rateMin', e.target.value)}
                  placeholder="Min"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                  min="0"
                  step="0.01"
                />
              </div>

              {/* Rate Range - Max */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Rate (₹)
                </label>
                <input
                  type="number"
                  value={filters.rateMax}
                  onChange={(e) => handleFilterChange('rateMax', e.target.value)}
                  placeholder="Max"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                  min="0"
                  step="0.01"
                />
              </div>

              {/* Quantity Range - Min */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Quantity
                </label>
                <input
                  type="number"
                  value={filters.quantityMin}
                  onChange={(e) => handleFilterChange('quantityMin', e.target.value)}
                  placeholder="Min"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                  min="0"
                />
              </div>

              {/* Quantity Range - Max */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Quantity
                </label>
                <input
                  type="number"
                  value={filters.quantityMax}
                  onChange={(e) => handleFilterChange('quantityMax', e.target.value)}
                  placeholder="Max"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                  min="0"
                />
              </div>
            </div>

            {/* Active Filters Summary */}
            {hasActiveFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-600">
                    Active filters: {[
                      searchTerm && 'Search',
                      filters.status !== 'all' && 'Status',
                      filters.dateRange !== 'all' && (filters.dateRange === 'specific_date' && filters.specificDate ? `Date: ${filters.specificDate}` : 'Date'),
                      filters.rateMin && `Rate ≥ ₹${filters.rateMin}`,
                      filters.rateMax && `Rate ≤ ₹${filters.rateMax}`,
                      filters.quantityMin && `Qty ≥ ${filters.quantityMin}`,
                      filters.quantityMax && `Qty ≤ ${filters.quantityMax}`
                    ].filter(Boolean).join(', ')}
                  </div>
                  <div className="text-sm text-gray-500">
                    Showing {filteredOrders.length} of {orders.length} orders
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Orders List */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Orders</h3>
        </div>
        <div className="overflow-hidden">
          {filteredOrders.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              <ShoppingBagIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p>{hasActiveFilters ? 'No orders match your filters.' : 'No orders found.'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      S.No.
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mobile
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Conversion Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrders.map((order, index) => (
                    <tr key={order.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {highlightSearchTerm(order.leads?.name || 'Unknown', searchTerm)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {highlightSearchTerm(order.leads?.phone || 'Unknown', searchTerm)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(order.conversion_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {highlightSearchTerm(order.quantity?.toString() || '-', searchTerm)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {order.rate ? `₹${highlightSearchTerm(order.rate.toString(), searchTerm)}` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                          {highlightSearchTerm(order.status?.replace('_', ' ').toUpperCase() || 'PENDING', searchTerm)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => openOrderDetails(order)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Order Details"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleOrderAction(order)}
                            className="text-primary-600 hover:text-primary-900"
                            title="Update Address & Status"
                          >
                            <MapPinIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteOrder(order.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete Order"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Order Modal */}
      {showOrderModal && selectedOrder && (
        <OrderModal
          order={selectedOrder}
          onSave={updateOrderAddress}
          onClose={() => setShowOrderModal(false)}
          formData={orderFormData}
          setFormData={setOrderFormData}
        />
      )}
    </div>
  );
};

// Order Modal Component
const OrderModal = ({ order, onSave, onClose, formData, setFormData }) => {
  // If formData and setFormData are provided from parent, use them
  // Otherwise, use local state (for backward compatibility)
  const [localFormData, setLocalFormData] = useState({
    address_line_1: order.address_line_1 || '',
    address_line_2: order.address_line_2 || '',
    city: order.city || '',
    state: order.state || '',
    pin: order.pin || '',
    status: order.status || 'pending_shipping',
    quantity: order.quantity || '',
    rate: order.rate || ''
  });

  // Use parent formData if provided, otherwise use local state
  const currentFormData = formData || localFormData;
  const setCurrentFormData = setFormData || setLocalFormData;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(order.id, currentFormData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Order Details - {order.leads?.name}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="address_line_1" className="block text-sm font-medium text-gray-700 mb-1">
                Address Line 1
              </label>
              <input
                type="text"
                id="address_line_1"
                name="address_line_1"
                value={currentFormData.address_line_1}
                onChange={handleChange}
                className="input-field"
                placeholder="Enter address line 1"
              />
            </div>

            <div>
              <label htmlFor="address_line_2" className="block text-sm font-medium text-gray-700 mb-1">
                Address Line 2
              </label>
              <input
                type="text"
                id="address_line_2"
                name="address_line_2"
                value={currentFormData.address_line_2}
                onChange={handleChange}
                className="input-field"
                placeholder="Enter address line 2"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={currentFormData.city}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="Enter city"
                />
              </div>
              <div>
                <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value={currentFormData.state}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="Enter state"
                />
              </div>
            </div>

            <div>
              <label htmlFor="pin" className="block text-sm font-medium text-gray-700 mb-1">
                PIN Code
              </label>
              <input
                type="text"
                id="pin"
                name="pin"
                value={currentFormData.pin}
                onChange={handleChange}
                className="input-field"
                placeholder="Enter PIN code"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity (No of Boxes)
                </label>
                <input
                  type="number"
                  id="quantity"
                  name="quantity"
                  value={currentFormData.quantity}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="Enter quantity"
                  min="1"
                />
              </div>
              <div>
                <label htmlFor="rate" className="block text-sm font-medium text-gray-700 mb-1">
                  Rate
                </label>
                <input
                  type="number"
                  id="rate"
                  name="rate"
                  value={currentFormData.rate}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="Enter rate"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={currentFormData.status}
                onChange={handleChange}
                className="input-field"
              >
                <option value="pending_shipping">Pending Shipping</option>
                <option value="shipped">Shipped</option>
                <option value="out_for_delivery">Out For Delivery</option>
                <option value="delivered">Delivered</option>
                <option value="not_redeemed">Not Redeemed</option>
                <option value="redeemed">Redeemed</option>
                <option value="returned">Returned</option>
              </select>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                Update Order
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Orders;
